// src/utils/staffTimesheet.js
//
// Pass 83C — the ONE canonical weekly staff-timesheet domain. Pure, framework-free, fully testable. It is
// LIGHT STAFF ADMIN (worked-time attestation), NOT payroll: there are NO rates/wages/commission/price fields
// anywhere in this module, and it NEVER reads financeData.
//
// THREE DISTINCT TRUTHS (never collapsed):
//   1. SCHEDULED/EXPECTED WORK — canonical bookings (what was planned; NOT proof it happened).
//   2. DELIVERED LESSON EVIDENCE — canonical lessonInstances via isDeliveryBearing() (real teaching-delivery).
//   3. STAFF ATTESTATION — the weekly timesheet: the member confirms the week's actual worked time.
// `booking.status` is NEVER proof of delivery (Pass 66 invariant). This module READS existing evidence only —
// it never creates lessonInstances, starts/completes lessons, or writes back to source sheets (Pass 82D).

import { isClientBooking, isLiveBooking } from './scheduleModel';
import { isGridHold } from './operationsWorkspace';
import { isDeliveryBearing } from './lessonHandoff';
import { zonedWeekStart, zonedShiftDays, zonedDateKey, wallClockToInstant } from './opsTime';

export const TIMESHEET_STATUS = { DRAFT: 'draft', SUBMITTED: 'submitted', REOPENED: 'reopened' };
// Line provenance (§30).
export const SOURCE_TYPE = { LESSON_INSTANCE: 'lesson_instance', SCHEDULED_WORK: 'scheduled_work', MANUAL: 'manual' };
// Whether a line already has genuine delivery evidence, or a human still needs to confirm the work happened.
// UPCOMING (Pass 83C.1 §17) = a scheduled booking later than now: it is not yet worked, not attestable, and
// never counts toward totals or blocks submission.
export const EVIDENCE = { DELIVERED: 'delivered', NEEDS_CONFIRMATION: 'needs_confirmation', MANUAL: 'manual', UPCOMING: 'upcoming' };
// Pass 83C.1 §16 — domain errors that block submission regardless of the UI.
export const SUBMIT_ERROR = { WEEK_OPEN: 'TIMESHEET_WEEK_OPEN', UNRESOLVED: 'TIMESHEET_UNRESOLVED' };
// The staff decision on a line. '' = unresolved (blocks submission for a closed week).
export const DECISION = { UNRESOLVED: '', AS_SCHEDULED: 'as_scheduled', ADJUSTED: 'adjusted', NOT_WORKED: 'not_worked' };
export const WORK_KIND = { TEACHING: 'teaching', OTHER: 'other' };

const str = (v) => String(v == null ? '' : v).trim();
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

function toDate(v) {
  if (!v) return null;
  if (typeof v?.toDate === 'function') return v.toDate();
  if (v?.seconds != null) return new Date(v.seconds * 1000);
  if (v instanceof Date) return v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

// ── Deterministic identity (§9/§10) — org + canonical member business userId + week start. No email/name. ──
export function timesheetWeekId(organizationId, memberUserId, weekStartKey) {
  const org = str(organizationId); const uid = str(memberUserId); const wk = str(weekStartKey);
  if (!org || !uid || !wk) return '';
  return `${org}::${uid}::${wk}`;
}

// ── Week identity in the ORG timezone (§11/§12). Monday-based by default; DST-safe via zoned* calendar math. ──
export function weekIdentity(reference, timeZone, { weekStartsOn = 1 } = {}) {
  const tz = timeZone || 'UTC';
  const ref = toDate(reference) || new Date();
  // zonedWeekStart is Monday-00:00 of the zoned week. weekStartsOn is accepted for future config; default Monday.
  let start = zonedWeekStart(ref, tz);
  if (weekStartsOn !== 1) {
    const delta = ((weekStartsOn - 1) + 7) % 7; // shift Monday-anchored start forward to the configured day
    if (delta) start = zonedShiftDays(start, delta, tz);
    // if the reference now falls before the shifted start, step back one week
    if (ref.getTime() < start.getTime()) start = zonedShiftDays(start, -7, tz);
  }
  const endExclusive = zonedShiftDays(start, 7, tz);
  return {
    weekStartKey: zonedDateKey(start, tz),
    weekEndKey: zonedDateKey(zonedShiftDays(start, 6, tz), tz), // inclusive last day (Sunday)
    weekStartMs: start.getTime(),
    weekEndExclusiveMs: endExclusive.getTime(),
    timezone: tz,
  };
}

// A required member must be a real production account (never the TEST fixture) and flagged for the season.
export function memberTimesheetRequired(member = {}, seasonId = '') {
  if (member.testAccount === true) return false;
  const season = seasonId && member.seasonMemberships ? member.seasonMemberships[seasonId] : null;
  return !!(season && season.timesheetRequired === true);
}

// Worked-time candidate: a live, non-hold, non-RDO booking (teaching OR internal staff work). Excludes
// RDO/timeoff, grid holds / provisional occupancy, availability blocks, cancelled/retired records (§24).
export function isRdoOrHold(b = {}) {
  return isGridHold(b) || b.scheduleEntryType === 'blocked' || b.activityType === 'timeoff' || b.activityType === 'rdo';
}
export function isWorkedTimeCandidate(b = {}) {
  return isLiveBooking(b) && !isRdoOrHold(b);
}
export function workKindOf(b = {}) {
  return isClientBooking(b) ? WORK_KIND.TEACHING : WORK_KIND.OTHER;
}

// Scheduled minutes for a booking: explicit serviceDurationSnapshot, else endAt−startAt.
export function bookingScheduledMinutes(b = {}) {
  const snap = num(b.serviceDurationSnapshot);
  if (snap > 0) return snap;
  const s = toDate(b.startAt); const e = toDate(b.endAt);
  if (s && e && e.getTime() > s.getTime()) return Math.round((e.getTime() - s.getTime()) / 60000);
  return 0;
}

function bookingDayKey(b, tz) {
  const s = toDate(b.startAt);
  if (s) return zonedDateKey(s, tz);
  return str(b.serviceDate);
}
function inWeek(b, week, tz) {
  const key = bookingDayKey(b, tz);
  return !!key && key >= week.weekStartKey && key <= week.weekEndKey;
}

// A short, PII-minimal work label (§29) — activity/service, never the guest name.
export function activityLabelOf(b = {}) {
  if (isClientBooking(b)) return str(b.bookingType || b.serviceNameSnapshot || b.serviceType) || 'Lesson';
  return str(b.activityType || b.scheduleEntryType || b.serviceType) || 'Work';
}

// ── Derive the weekly draft from canonical evidence (§17/§18/§19/§22/§23/§25). Read-only. ──
// bookings/lessons are the member's org-scoped records; `lessons` are lessonInstances keyed by bookingId
// (Map or array). Returns candidate LINES; nothing is persisted until the member adjusts or submits.
export function deriveTimesheetCandidates({ bookings = [], lessons = [], week, timeZone, now = new Date() }) {
  const tz = timeZone || (week && week.timezone) || 'UTC';
  const nowMs = now && now.getTime ? now.getTime() : Number(now) || Date.now();
  const lessonByBooking = new Map();
  const asArray = Array.isArray(lessons) ? lessons : (lessons && typeof lessons.values === 'function' ? [...lessons.values()] : []);
  for (const l of asArray) { if (l && l.bookingId) lessonByBooking.set(str(l.bookingId), l); }

  const lines = [];
  const seen = new Set();
  const startMsOf = (b) => { const s = toDate(b.startAt); return s ? s.getTime() : (b.serviceDate ? wallClockToInstant(b.serviceDate, '23:59', tz).getTime() : null); };
  for (const b of bookings) {
    const bookingId = str(b.id || b.bookingId);
    if (!bookingId || seen.has(bookingId)) continue;
    const lesson = lessonByBooking.get(bookingId) || null;
    const deliveryBearing = !!(lesson && isDeliveryBearing(lesson));
    const candidate = isWorkedTimeCandidate(b);
    // §25 — a cancelled/retired booking that nonetheless has delivery-bearing evidence is surfaced as an
    // exception (never silently dropped). A live worked-time candidate is always included.
    if (!candidate && !deliveryBearing) continue;
    if (!inWeek(b, week, tz)) continue;
    seen.add(bookingId);

    const scheduledMinutes = bookingScheduledMinutes(b);
    const kind = workKindOf(b);
    const scheduleChanged = !isLiveBooking(b); // delivered-but-cancelled exception
    // §17/§18 — a booking later than now is UPCOMING: not attestable, not counted, not overdue. Delivered
    // evidence overrides "upcoming" (it already happened).
    const st = startMsOf(b);
    const upcoming = !deliveryBearing && st != null && st > nowMs;
    lines.push({
      key: bookingId,
      sourceType: deliveryBearing ? SOURCE_TYPE.LESSON_INSTANCE : SOURCE_TYPE.SCHEDULED_WORK,
      bookingId,
      lessonInstanceId: lesson ? str(lesson.lessonId || lesson.id) : '',
      dateKey: bookingDayKey(b, tz),
      kind,
      activityLabel: activityLabelOf(b),
      discipline: isClientBooking(b) ? str(b.discipline) : '',
      scheduledMinutes,
      durationBasis: 'scheduled',
      // delivery-bearing teaching lines are prefilled + resolved (as_scheduled); upcoming lines wait; every
      // other PAST candidate needs explicit staff confirmation (§18/§19/§23). Delivered+cancelled → confirm.
      evidence: upcoming ? EVIDENCE.UPCOMING : deliveryBearing ? EVIDENCE.DELIVERED : EVIDENCE.NEEDS_CONFIRMATION,
      decision: (deliveryBearing && !scheduleChanged) ? DECISION.AS_SCHEDULED : DECISION.UNRESOLVED,
      attestedMinutes: (deliveryBearing && !scheduleChanged) ? scheduledMinutes : null,
      scheduleChanged,
      upcoming,
    });
  }
  // §27 — an instructor-owned DELIVERY-BEARING lessonInstance whose booking is not present in the booking
  // result (reassigned/retired) must NOT vanish: surface it as an exception line needing confirmation. Never
  // fabricate a duration.
  for (const l of asArray) {
    const bid = str(l && l.bookingId);
    if (!bid || seen.has(bid)) continue;
    if (!isDeliveryBearing(l)) continue;
    const dayKey = l.scheduledAt ? zonedDateKey(toDate(l.scheduledAt), tz) : str(l.serviceDate);
    if (!dayKey || dayKey < week.weekStartKey || dayKey > week.weekEndKey) continue;
    seen.add(bid);
    lines.push({
      key: bid, sourceType: SOURCE_TYPE.LESSON_INSTANCE, bookingId: bid, lessonInstanceId: str(l.lessonId || l.id),
      dateKey: dayKey, kind: WORK_KIND.TEACHING, activityLabel: str(l.serviceType || l.sport) || 'Lesson', discipline: '',
      scheduledMinutes: 0, durationBasis: 'unknown',
      evidence: EVIDENCE.NEEDS_CONFIRMATION, decision: DECISION.UNRESOLVED, attestedMinutes: null,
      scheduleChanged: true, scheduleMissing: true, upcoming: false,
    });
  }
  lines.sort((a, b) => (a.dateKey < b.dateKey ? -1 : a.dateKey > b.dateKey ? 1 : 0));
  return lines;
}

// Merge persisted staff decisions + manual lines onto freshly-derived candidates so a source refresh never
// discards work the member already resolved (§61). Submitted snapshots are frozen (handled by the caller).
export function mergeTimesheetLines(derivedLines = [], persistedLines = []) {
  const byKey = new Map();
  for (const d of derivedLines) byKey.set(d.key, { ...d });
  const extra = [];
  for (const p of persistedLines) {
    if (p.sourceType === SOURCE_TYPE.MANUAL) { extra.push({ ...p }); continue; }
    const base = byKey.get(p.key);
    if (!base) {
      // Pass 83C.1 §25/§26 — the source line vanished from current evidence (reassigned/retired) but the
      // staff already recorded a decision: PRESERVE it (never silently delete), flagged sourceMissing for review.
      extra.push({
        key: p.key, sourceType: p.sourceType, bookingId: str(p.bookingId), lessonInstanceId: str(p.lessonInstanceId),
        dateKey: str(p.dateKey), kind: p.kind || WORK_KIND.OTHER, activityLabel: str(p.activityLabel) || 'Work',
        discipline: str(p.discipline), scheduledMinutes: num(p.scheduledMinutes), durationBasis: p.durationBasis || 'scheduled',
        evidence: EVIDENCE.NEEDS_CONFIRMATION, decision: p.decision || DECISION.UNRESOLVED,
        attestedMinutes: p.attestedMinutes != null ? p.attestedMinutes : null, adjustmentReason: str(p.adjustmentReason),
        scheduleChanged: true, sourceMissing: true, upcoming: false,
      });
      continue;
    }
    // keep the member's decision/attestation; refresh the objective scheduled facts from evidence
    byKey.set(p.key, {
      ...base,
      decision: p.decision || base.decision,
      attestedMinutes: p.attestedMinutes != null ? p.attestedMinutes : base.attestedMinutes,
      adjustmentReason: p.adjustmentReason || '',
    });
  }
  return [...byKey.values(), ...extra].sort((a, b) => (str(a.dateKey) < str(b.dateKey) ? -1 : str(a.dateKey) > str(b.dateKey) ? 1 : 0));
}

// A line's attested minutes (0 when not worked / unresolved-needs-confirmation).
export function lineAttestedMinutes(line = {}) {
  if (line.decision === DECISION.NOT_WORKED) return 0;
  if (line.sourceType === SOURCE_TYPE.MANUAL) return num(line.attestedMinutes);
  if (line.decision === DECISION.AS_SCHEDULED) return num(line.attestedMinutes != null ? line.attestedMinutes : line.scheduledMinutes);
  if (line.decision === DECISION.ADJUSTED) return num(line.attestedMinutes);
  return 0; // unresolved
}

export function timesheetTotals(lines = []) {
  let teaching = 0; let other = 0;
  for (const l of lines) {
    const m = lineAttestedMinutes(l);
    if (!m) continue;
    if (l.kind === WORK_KIND.TEACHING) teaching += m; else other += m;
  }
  return { teachingMinutes: teaching, otherMinutes: other, totalAttestedMinutes: teaching + other };
}

// A line still needs confirmation when it is a PAST source-backed candidate with no staff decision (§32).
// UPCOMING lines (future) are NOT unresolved — they are simply not yet workable (§17).
export function isUnresolved(line = {}) {
  if (line.sourceType === SOURCE_TYPE.MANUAL) return false;
  if (line.evidence === EVIDENCE.UPCOMING || line.upcoming === true) return false;
  return !line.decision || line.decision === DECISION.UNRESOLVED;
}
export function unresolvedLines(lines = []) {
  return lines.filter(isUnresolved);
}

// A week is CLOSED (submittable) once its exclusive end is at/before now (§32/§34); a CURRENT week is progress-only.
export function isWeekClosed(week, now = new Date()) {
  return !!week && (now.getTime ? now.getTime() : now) >= week.weekEndExclusiveMs;
}

// Submission readiness (§15/§32/§33): a CURRENT (not-yet-closed) week is NEVER submittable, even with zero
// candidates or all resolved. A CLOSED week is submittable only when no PAST candidate remains unresolved.
export function submissionReadiness(lines = [], week, now = new Date()) {
  if (!isWeekClosed(week, now)) return { ready: false, reason: 'current_week', unresolvedCount: unresolvedLines(lines).length, closed: false };
  const unresolved = unresolvedLines(lines);
  if (unresolved.length > 0) return { ready: false, reason: 'unresolved', unresolvedCount: unresolved.length, closed: true };
  return { ready: true, reason: 'closed_resolved', unresolvedCount: 0, closed: true };
}

// §35 — a reusable domain guard the writer AND the UI both use, so a non-closed / unresolved submission is
// refused regardless of the React button state. Returns { ok, error } (error ∈ SUBMIT_ERROR).
export function validateTimesheetSubmission(lines = [], week, now = new Date()) {
  if (!isWeekClosed(week, now)) return { ok: false, error: SUBMIT_ERROR.WEEK_OPEN };
  if (unresolvedLines(lines).length > 0) return { ok: false, error: SUBMIT_ERROR.UNRESOLVED };
  return { ok: true, error: '' };
}

// Frozen submission snapshot (§31/§36). No wages/prices — minutes + provenance only.
export function buildSubmissionSnapshot(lines = [], { revision = 1 } = {}) {
  const frozen = lines
    .filter((l) => lineAttestedMinutes(l) > 0 || l.decision === DECISION.NOT_WORKED)
    .map((l) => ({
      key: l.key, sourceType: l.sourceType, bookingId: str(l.bookingId), lessonInstanceId: str(l.lessonInstanceId),
      dateKey: l.dateKey, kind: l.kind, activityLabel: l.activityLabel, discipline: str(l.discipline),
      scheduledMinutes: num(l.scheduledMinutes), attestedMinutes: lineAttestedMinutes(l),
      decision: l.decision || (l.sourceType === SOURCE_TYPE.MANUAL ? 'manual' : DECISION.AS_SCHEDULED),
      adjustmentReason: str(l.adjustmentReason), evidence: l.evidence,
    }));
  const totals = timesheetTotals(lines);
  return { lines: frozen, totalAttestedMinutes: totals.totalAttestedMinutes, teachingMinutes: totals.teachingMinutes, otherMinutes: totals.otherMinutes, revision };
}

// ── CSV export (§54/§55) — hours/evidence only; NEVER wage/price/commission/guest contact. ──
export const TIMESHEET_EXPORT_COLUMNS = ['weekStart', 'memberUserId', 'memberName', 'workDate', 'activityType', 'sourceType', 'sourceReference', 'attestedMinutes', 'decision', 'submittedAt'];
export function timesheetExportRows(timesheets = [], memberNameById = {}) {
  const rows = [];
  for (const t of timesheets) {
    if (t.status !== TIMESHEET_STATUS.SUBMITTED) continue; // submitted attestation only
    const name = str(memberNameById[t.memberUserId] || t.memberName);
    for (const l of (t.lines || [])) {
      rows.push({
        weekStart: t.weekStartKey, memberUserId: t.memberUserId, memberName: name,
        workDate: l.dateKey, activityType: l.activityLabel, sourceType: l.sourceType,
        sourceReference: l.lessonInstanceId || l.bookingId || '', attestedMinutes: l.attestedMinutes,
        decision: l.decision, submittedAt: t.submittedAtIso || '',
      });
    }
  }
  return rows;
}
export function toCsv(columns, rows) {
  const esc = (v) => { const s = String(v == null ? '' : v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  return [columns.join(','), ...rows.map((r) => columns.map((c) => esc(r[c])).join(','))].join('\n');
}
